/**
 * @file apps/web/src/pages/DiscoveryPage.tsx
 * @description Discovery skill tree. Module tabs (P2P/OTC/GL_RTR/HCM)
 * across the top, collapsible process-area groups underneath, each
 * containing the NetSuite-rule-seeded nodes for this project. Coverage
 * summary bar shows % reviewed per module. An optional Upload panel feeds
 * the Discovery-node suggestions surfaced here — a workshop recording is a
 * Discovery-phase input (it's what the skill tree gets reviewed against),
 * not a UAT one, so this is where it lives (moved from UatPage.tsx). Node
 * dispositions themselves stay entirely manual — see DiscoveryPanel.tsx's
 * suggestion banner and its header comment for why suggestions are never
 * auto-committed.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, AlertCircle, Sparkles, Mic2, ChevronUp, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { discoveryApi, audioApi, type AudioUploadKickoff } from '../lib/api.js';
import { Badge, type BadgeVariant } from '../components/Badge.js';
import { DiscoveryPanel, type DiscoveryNode } from '../components/DiscoveryPanel.js';
import { UploadZone } from '../components/UploadZone.js';
import { ProcessingStatus, type UploadStatus } from '../components/ProcessingStatus.js';
import { notifyProjectOverviewChanged } from '../lib/projectOverviewEvents.js';
import { classifyLatestUpload, describeLatestUpload, type LatestUploadKind } from '../lib/latestUpload.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ModuleSummary {
  module:        string;
  total:         number;
  reviewed:      number;
  byDisposition: Record<string, number>;
}

const MODULES = ['P2P', 'OTC', 'GL_RTR', 'HCM'] as const;
const MODULE_LABELS: Record<string, string> = { P2P: 'P2P', OTC: 'OTC', GL_RTR: 'GL/RTR', HCM: 'HCM' };

// ---------------------------------------------------------------------------
// Coverage bar
// ---------------------------------------------------------------------------

function CoverageBar({ summary }: { summary: ModuleSummary[] }) {
  return (
    <div className="card p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
      {MODULES.map((mod) => {
        const s = summary.find((x) => x.module === mod);
        const pct = s && s.total > 0 ? Math.round((s.reviewed / s.total) * 100) : 0;
        return (
          <div key={mod}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-mono text-muted">{MODULE_LABELS[mod]}</span>
              <span className="text-[11px] font-mono text-ink">{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray overflow-hidden">
              <div className="h-full bg-blue transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[10px] font-mono text-muted/70 mt-1">
              {s?.reviewed ?? 0} / {s?.total ?? 0} reviewed
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Node row
// ---------------------------------------------------------------------------

function NodeRow({ node, onClick }: { node: DiscoveryNode; onClick: () => void }) {
  // Only nudge nodes nobody has decided on yet — once a human has set a
  // real disposition, the suggestion columns are already cleared server-side
  // anyway (see PATCH /projects/:id/discovery/:nodeId), but this guards the
  // brief window before that round-trip too.
  const hasSuggestion = node.disposition === 'not_reviewed' && !!node.suggested_disposition;

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-gray/40 transition-colors"
    >
      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        {hasSuggestion && (
          <Sparkles size={12} className="text-warning shrink-0" aria-label="Workshop suggestion available" />
        )}
        <div className="min-w-0">
          <p className="text-sm text-ink truncate">{node.process_name}</p>
          {node.notes && (
            <p className="text-xs text-muted truncate mt-0.5">{node.notes}</p>
          )}
        </div>
      </div>
      <Badge variant={node.disposition as BadgeVariant} size="xs" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Process-area collapsible group
// ---------------------------------------------------------------------------

function ProcessAreaGroup({
  area, nodes, onNodeClick,
}: {
  area:        string;
  nodes:       DiscoveryNode[];
  onNodeClick: (node: DiscoveryNode) => void;
}) {
  const [open, setOpen] = useState(true);
  const reviewedCount = nodes.filter((n) => n.disposition !== 'not_reviewed').length;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-3 hover:bg-gray/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-muted" /> : <ChevronRight size={14} className="text-muted" />}
          <span className="text-sm font-medium text-ink">{area}</span>
        </div>
        <span className="text-[11px] font-mono text-muted">{reviewedCount} / {nodes.length}</span>
      </button>
      {open && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-1.5 space-y-0.5">
          {nodes.map((n) => <NodeRow key={n.id} node={n} onClick={() => onNodeClick(n)} />)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DiscoveryPage
// ---------------------------------------------------------------------------

export function DiscoveryPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { devHeaders } = useAuth();

  const [grouped, setGrouped]   = useState<Record<string, Record<string, DiscoveryNode[]>>>({});
  const [summary, setSummary]   = useState<ModuleSummary[]>([]);
  const [activeModule, setActiveModule] = useState<string>('P2P');
  const [activeNode, setActiveNode]     = useState<DiscoveryNode | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Upload panel — moved here from UatPage.tsx (a workshop recording is a
  // Discovery-phase input, not a UAT one; see this file's header comment).
  const [showUpload, setShowUpload] = useState(false);
  const [uploadId, setUploadId]     = useState<string | null>(null);

  // The project's most recent upload, for the status strip shown when the
  // upload panel is closed (finished within 24h, or stalled). An upload that is
  // still genuinely processing is resumed in the panel itself instead — see the
  // effect below and lib/latestUpload.ts.
  const [latestStrip, setLatestStrip] = useState<{ filename: string; stage: string; text: string; kind: LatestUploadKind; uploadId: string } | null>(null);

  // The sidebar's "Upload Recording" link lands here as ?upload=1 — open the
  // panel directly, then drop the param so a refresh doesn't keep reopening it
  // (and clicking the link again, now from a clean URL, opens it again).
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  useEffect(() => {
    if (!searchParams.get('upload')) return;
    setShowUpload(true);
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('upload'); return next; }, { replace: true });
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a refresh the component state that tracked the upload is gone; ask
  // the server for the latest one. Still processing -> reopen the panel and
  // resume polling. Finished recently, or stalled -> show the status strip.
  // A fresh upload started on this page always wins over what the server said.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    audioApi.latest(projectId, devHeaders)
      .then((latest) => {
        if (cancelled || !latest) return;
        const kind = classifyLatestUpload(latest);
        if (kind === 'resume') {
          setUploadId((current) => current ?? latest.id);
          setShowUpload(true);
        } else if (kind === 'recent' || kind === 'stalled') {
          setLatestStrip({
            filename: latest.filename, stage: latest.stage, kind, uploadId: latest.id,
            text: describeLatestUpload(latest, kind),
          });
        }
      })
      .catch(() => { /* status is a convenience — never block the page on it */ });
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [nodesData, summaryData] = await Promise.all([
        discoveryApi.get(projectId, devHeaders),
        discoveryApi.getSummary(projectId, devHeaders),
      ]);
      setGrouped(nodesData as Record<string, Record<string, DiscoveryNode[]>>);
      setSummary(summaryData as ModuleSummary[]);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load discovery data');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  const handleSaveNode = async (nodeId: string, updates: { disposition?: string; notes?: string }) => {
    if (!projectId) return;
    const updated = await discoveryApi.updateNode(projectId, nodeId, updates, devHeaders) as DiscoveryNode;
    // The write is committed — tell the sidebar to re-read the phase gates NOW
    // (not after the summary refetch below, which could fail independently),
    // so Design unlocks the moment the last node is reviewed, no refresh.
    notifyProjectOverviewChanged(projectId);
    setGrouped((prev) => {
      const next = { ...prev };
      for (const mod of Object.keys(next)) {
        for (const area of Object.keys(next[mod]!)) {
          next[mod]![area] = next[mod]![area]!.map((n) => n.id === nodeId ? { ...n, ...updated } : n);
        }
      }
      return next;
    });
    await discoveryApi.getSummary(projectId, devHeaders).then((s) => setSummary(s as ModuleSummary[]));
  };

  // Audio upload via UploadZone — resolves as soon as the file is stored
  // and a transcription job exists; ProcessingStatus (below) polls
  // GET /audio/:id from there. projectId is already appended by
  // UploadZone itself (has to be, before the file field — see its own
  // comment), so no need to add it again here.
  const handleUpload = async (formData: FormData): Promise<AudioUploadKickoff> => {
    const kickoff = await audioApi.upload(formData, devHeaders);
    setUploadId(kickoff.uploadId);
    setLatestStrip(null); // the strip described the PREVIOUS upload; this one is now the live one
    return kickoff;
  };

  // Polling fn for ProcessingStatus — maps GET /audio/:id's raw
  // (snake_case, backend-shaped) response onto the camelCase UploadStatus
  // shape ProcessingStatus.tsx expects.
  const pollStatus = async (id: string): Promise<UploadStatus> => {
    const data = await audioApi.get(id, devHeaders);
    return {
      uploadId: data.id,
      stage: data.stage,
      transcriptionId: data.transcription_id,
      transcriptText: data.transcript_text,
      requirementsExtracted: data.requirements_extracted,
      discoverySuggestionsCreated: data.discovery_suggestions_created,
      gapsCreated: data.gaps_created,
      durationSecs: data.duration_secs ?? undefined,
      language: data.language ?? undefined,
      errorMessage: data.error_message,
    };
  };

  const areasForActiveModule = grouped[activeModule] ?? {};
  const hasAnyNodes = Object.keys(grouped).length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Discovery</h2>
          <p className="text-xs font-mono text-muted mt-0.5">
            Walk every NetSuite capability and record its disposition before UAT.
          </p>
        </div>
        {!showUpload && (
          <button onClick={() => setShowUpload(true)} className="btn-secondary text-sm shrink-0">
            <Mic2 size={14} /> Upload Recording
          </button>
        )}
      </div>

      {!showUpload && latestStrip && (
        <div className="card px-4 py-3 flex items-center justify-between gap-3 max-w-xl">
          <div className="flex items-center gap-2 min-w-0 text-sm">
            {latestStrip.kind === 'stalled'
              ? <AlertCircle size={14} className="text-warning shrink-0" />
              : latestStrip.stage === 'done'
                ? <CheckCircle size={14} className="text-success shrink-0" />
                : <Loader2 size={14} className="text-muted shrink-0" />}
            <p className="truncate text-ink/80" title={latestStrip.text}>{latestStrip.text}</p>
          </div>
          {latestStrip.kind !== 'stalled' && (
            <button
              onClick={() => { setUploadId(latestStrip.uploadId); setShowUpload(true); }}
              className="btn-secondary text-xs shrink-0"
            >
              View details
            </button>
          )}
        </div>
      )}

      {showUpload && (
        <div className="card p-5 space-y-4 max-w-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-sm font-semibold text-ink">Upload Workshop Recording</h3>
              <p className="text-xs text-muted mt-0.5">
                Transcribes the recording, extracts requirements, and suggests dispositions for matching nodes below — nothing is set automatically.
              </p>
            </div>
            <button onClick={() => setShowUpload(false)} className="btn-icon text-muted shrink-0" aria-label="Close">
              <ChevronUp size={16} />
            </button>
          </div>

          <UploadZone
            projectId={projectId ?? ''}
            uploadFn={handleUpload}
            onComplete={(result) => setUploadId(result.uploadId)}
          />

          {uploadId && (
            <div className="pt-2 border-t border-[rgba(8,17,31,0.06)]">
              <p className="label mb-3">Pipeline Status</p>
              <ProcessingStatus
                uploadId={uploadId}
                pollFn={pollStatus}
                projectId={projectId}
                onComplete={() => load()}
                onReviewSuggestions={() => {
                  // Already on the Discovery page: close the panel and bring the
                  // tree (with its sparkle markers on nodes that have a
                  // suggestion) into view instead of reloading the route.
                  setShowUpload(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="card h-64 animate-pulse bg-gray/60" />
      ) : !hasAnyNodes ? (
        <div className="empty-state py-16">
          <p className="text-sm text-muted">No discovery nodes for this project.</p>
          <p className="text-xs text-muted/70 mt-1">
            Discovery nodes are seeded automatically for NetSuite projects when they're created.
          </p>
        </div>
      ) : (
        <>
          <CoverageBar summary={summary} />

          <div className="flex gap-1 border-b border-[rgba(8,17,31,0.08)]">
            {MODULES.map((mod) => (
              <button
                key={mod}
                onClick={() => setActiveModule(mod)}
                className={[
                  'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                  activeModule === mod ? 'border-blue text-blue' : 'border-transparent text-muted hover:text-ink',
                ].join(' ')}
              >
                {MODULE_LABELS[mod]}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {Object.entries(areasForActiveModule).map(([area, nodes]) => (
              <ProcessAreaGroup key={area} area={area} nodes={nodes} onNodeClick={setActiveNode} />
            ))}
          </div>
        </>
      )}

      <DiscoveryPanel
        node={activeNode}
        projectId={projectId ?? ''}
        onClose={() => setActiveNode(null)}
        onSave={handleSaveNode}
      />
    </div>
  );
}
